import { supabase } from './supabaseClient';
import { ServiceTransaction } from '../types';
import {
    ProjectSaveConflictError,
    forgetProjectVersion,
    isProjectSaveConflict,
    knownProjectVersion,
    rememberProjectVersion,
} from './projectVersionAnchor';
import { describeError } from './errorMessage';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EINE PROTOKOLLZEILE AUS DEM JSON NEHMEN — die einzige Stelle
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Das Leistungsprotokoll liegt als Array in
 * `projects.data -> metadata -> globalData -> serviceLog`. Jede Zeile ist ein
 * Geschaeftsvorfall: sie darf dazukommen, sie darf sich aendern, verschwinden
 * darf sie nur auf einem der wenigen Wege, die sie ausdruecklich
 * zuruecknehmen.
 *
 * ─── Was der Riegel heute tut (Migration Nr. 44) ───────────────────────────
 * `service_log_guard_trg` (supabase/service_log_guard.sql) vergleicht bei
 * JEDEM Projekt-Update OLD gegen NEW und haengt jede Zeile, die in OLD steht
 * und in NEW fehlt, STILL wieder an — ausser sie traegt eine
 * `bookingReference`, die mit `BU-` oder `ALT-` beginnt. Diese eine Ausnahme
 * existiert nur, weil es drei legitime Pfade gibt, die genau solche Zeilen
 * entfernen (Ruecknahme der Rechnungs-Rueckschreibung, Ruecknahme eines
 * Alt-Rechnungs-Nachtrags, Storno einer Position in der Buchhaltung).
 *
 * Dieselbe Ausnahme kostet aber Buchungszeilen, sobald irgendein Client einen
 * VERALTETEN Blob zurueckschreibt: ein zweiter Tab, ein Handy mit altem Stand,
 * ein Wiederholungs-Speichern aus einer alten Sitzung. Der Riegel sieht die
 * BU-/ALT-Zeile fehlen, haelt das fuer eine der drei Ruecknahmen und laesst
 * sie fallen. Am 31.08.2026 waren das 19 Zeilen in 5 Projekten.
 *
 * ─── Was Migration Nr. 66 daraus macht ─────────────────────────────────────
 * Der Riegel WIRFT dann bei jeder fehlenden BU-/ALT-Zeile. Entfernt wird
 * ausschliesslich noch serverseitig ueber
 *
 *     service_log_remove_entries(p_project_id uuid, p_entry_ids text[],
 *                                p_reason text) returns jsonb
 *
 * `security definer`, setzt den Notausgang `app.service_log_guard = 'off'`
 * intern, nimmt die Zeilen selbst aus `projects.data` und protokolliert sie
 * vollstaendig. Zurueck kommt, was wirklich entfernt wurde und was nicht
 * gefunden wurde.
 *
 * ─── DIE REIHENFOLGE: ERST DIE RPC, DANN DER SAVE ──────────────────────────
 * Sie ist nicht offensichtlich, und ein spaeterer Umbau wuerde sie umdrehen.
 *
 * Der Riegel vergleicht OLD gegen NEW. Speicherte ein Pfad zuerst den Blob
 * OHNE die Zeilen, saehe der Riegel sie in OLD stehen und in NEW fehlen — und
 * wuerfe. Der ganze Vorgang scheiterte. Laeuft dagegen die RPC zuerst, FEHLEN
 * die Zeilen danach bereits in OLD; der anschliessende Save ist fuer den
 * Riegel unauffaellig und schreibt nur noch den Rest (Mengen, Notiz, Status).
 *
 * Also ueberall und ausnahmslos:
 *
 *     1. removeServiceLogEntries(...)   — Zeilen serverseitig entfernen
 *     2. saveProjectToCloud(...)        — den uebrigen Stand speichern
 *     3. voidJournalEntries(...)        — Zweitschrift im Journal stilllegen
 *
 * Zu Schritt 3 und WARUM er hinten bleibt: `voidJournalEntries` kennt kein
 * Un-void. Liefe es vor der Entfernung und die Entfernung scheiterte, waere
 * die Journalzeile unwiderruflich stillgelegt, waehrend das JSON die Buchung
 * weiterfuehrt — eine Forderung, die eine spaetere Abstimmung fuer
 * zurueckgenommen haelt, ohne Weg zurueck. Andersherum bleibt bei einem
 * Fehlschlag hoechstens eine UEBERZAEHLIGE Journalzeile stehen; die meldet die
 * Abweichungssicht, und ein erneuter Aufruf holt sie nach. Sichtbar und
 * heilbar schlaegt still und endgueltig.
 *
 * ─── DER LOKALE STAND MUSS ZUM SERVERSTAND PASSEN ──────────────────────────
 * Die drei Pfade rechnen ihren neuen Stand rein funktional aus
 * (`planWriteBackRevert`, `revertBackfill`) und speichern ihn als ganzen Blob.
 * Genau dieser Blob muss die entfernten Zeilen bereits NICHT mehr enthalten —
 * sonst schriebe der Save sie unmittelbar nach der RPC wieder hinein. Der
 * Riegel winkte das als NEUANLAGE durch (er schuetzt nur gegen
 * Verschwinden), und die Buchung stuende doppelt im Protokoll: einmal in der
 * Ruecknahme protokolliert, einmal wieder da. `serviceLogWithout` bildet
 * diesen erwarteten Reststand und macht ihn pruefbar.
 *
 * ─── `legacy`: der Client wird VOR dem SQL ausgeliefert ────────────────────
 * Auf einer Umgebung ohne Nr. 66 muss sich die App byte-gleich wie heute
 * verhalten. Fehlt die Funktion (PGRST202 / „could not find the function"),
 * meldet `removeServiceLogEntries` `mode: 'legacy'` und wirft NICHT — dann
 * entfernt der nachfolgende Blob-Save die Zeilen wie bisher, und der alte
 * Riegel laesst sie ueber die BU-/ALT-Ausnahme ziehen. Dasselbe Muster wie
 * `cloudService.syncChildrenTransactional` und `serviceJournal.appendToJournal`.
 *
 * JEDER ANDERE Fehler wirft. Er darf nicht verschluckt werden: mit
 * ausgerollter Nr. 66 wuerde der folgende Save am Riegel scheitern, und ein
 * still weiterlaufender Vorgang liesse den Anwender glauben, die Ruecknahme
 * sei gelungen.
 *
 * ─── `reason` IST PFLICHT ──────────────────────────────────────────────────
 * Die RPC protokolliert ihn DAUERHAFT neben den entfernten Zeilen. Er ist
 * spaeter die einzige Auskunft darueber, warum eine Buchung aus dem Protokoll
 * verschwunden ist — geschrieben fuer jemanden, der Monate spaeter einen
 * Geldbetrag sucht. „revert" oder „fix" sind deshalb keine Begruendung; ein
 * brauchbarer Grund nennt den Vorgang und den Beleg, etwa
 * „Rechnungs-Rueckschreibung BU-INV-2026-0042 zurueckgenommen (Ausstellen
 * abgebrochen)". Ein leerer Grund ist ein Programmierfehler und wirft, bevor
 * irgendetwas geschrieben wird.
 *
 * ═══ MIGRATION NR. 118: EINE TRANSAKTION STATT ZWEIER ══════════════════════
 * Alles oben beschreibt den Weg mit ZWEI Schreibvorgaengen — RPC, dann Save.
 * Genau dazwischen lag Vorfall 26051025 (22.09.2026): die RPC hatte die Zeile
 * genommen, der Save traf den Anker nicht, die Bergung liess die Zaehler beim
 * Serverwert, und „erbracht = 4" blieb dreimal stehen. Der Anker-Fix vom
 * 22.09. macht das sichtbar; zu schliessen ist es nur serverseitig.
 *
 * Nr. 118 (`supabase/service_log_remove_entries_atomic.sql`) legt eine
 * SECHSSTELLIGE Ueberladung derselben Funktion an:
 *
 *     service_log_remove_entries(p_project_id uuid, p_entry_ids text[],
 *                                p_reason text, p_expected_updated_at timestamptz,
 *                                p_shortened jsonb, p_counters jsonb)
 *
 * Sie sperrt die Projektzeile, prueft `projects.updated_at` gegen den
 * mitgegebenen Anker (SQLSTATE 40001, wenn er nicht trifft), entfernt, KUERZT
 * (teilweise Ruecknahme: die Zeile bleibt unter ihrer id und traegt nur noch
 * den Rest) und schreibt `project_services.quantity_used` — alles in EINER
 * Transaktion. Zurueck kommt der neue `updated_at`, mit dem der Anker ohne
 * zweites Lesen nachgezogen wird.
 *
 * Aufgerufen wird sie, sobald `opts` uebergeben ist. Fehlt sie in der Datenbank
 * (PGRST202), faellt dieser Weg auf die dreistellige Fassung aus Nr. 66 zurueck
 * und meldet `countersWritten: false` — dann macht der Save es wie bisher. Der
 * Client darf deshalb VOR dem SQL ausgeliefert werden.
 *
 * Eine Position, die es relational (noch) nicht gibt, WIRFT dort nicht: sie
 * kommt als `counters_missing` zurueck (`countersMissing`), `countersWritten`
 * bleibt dann false, und der Save legt die Zeile wie immer selbst an. Ohne
 * diese Ausnahme verlore jedes Projekt mit unvollstaendigen Kindtabellen jede
 * Ruecknahme, weil der Wurf die Entfernung mitgenommen haette.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Name der RPC aus Migration Nr. 66 — und, sechsstellig, aus Nr. 118. */
export const SERVICE_LOG_REMOVAL_RPC = 'service_log_remove_entries';

/** SQLSTATE, mit dem Nr. 118 einen ueberholten Anker meldet. */
const SERIALIZATION_FAILURE = '40001';

/**
 * `rpc`    — Nr. 66 steht, die Zeilen sind serverseitig entfernt und
 *            protokolliert. Zaehler und Kuerzung bleiben Sache des Saves.
 * `atomic` — Nr. 118 steht: Entfernung, Kuerzung, Zaehler und Versionspruefung
 *            sind in EINER Transaktion gelaufen.
 * `legacy` — die RPC gibt es (noch) nicht; der Aufrufer geht den bisherigen
 *            Weg ueber den Blob-Save weiter. Kein Fehler, sondern der geplante
 *            Zustand zwischen Client-Deploy und SQL-Lauf.
 * `noop`   — es gab nichts zu tun (keine Zeile mit brauchbarer id, nichts zu
 *            kuerzen, kein Zaehler).
 */
export type ServiceLogRemovalMode = 'rpc' | 'atomic' | 'legacy' | 'noop';

export interface ServiceLogRemovalOutcome {
    mode: ServiceLogRemovalMode;
    /** ids, die der Server tatsaechlich aus dem Protokoll genommen hat. */
    removedIds: string[];
    /**
     * ids, die der Server im Protokoll NICHT gefunden hat.
     *
     * Kein Fehler: eine zweite Sitzung kann dieselbe Zeile bereits
     * zurueckgenommen haben, und eine Zeile, die es nicht gibt, muss auch nicht
     * verschwinden. Aber eine Auskunft, die nicht verloren gehen darf — sie
     * wird protokolliert. Nr. 118 meldet hier auch die ids, die GEKUERZT werden
     * sollten und nicht (mehr) im Protokoll standen.
     */
    missingIds: string[];
    /**
     * ids, deren Menge der Server wirklich gesenkt hat (teilweise Ruecknahme).
     *
     * Nur im Modus `atomic` gefuellt: auf den anderen Wegen kuerzt erst der
     * Blob-Save, und was er schreibt, weiss diese Stelle nicht.
     */
    shortenedIds: string[];
    /**
     * Hat der Server `project_services.quantity_used` mitgeschrieben?
     *
     * Die Frage ist keine Kosmetik: sie entscheidet, was ein Aufrufer nach
     * einem gescheiterten Save melden muss. `true` heisst „Protokoll UND
     * Leistungsstand stehen schon, es fehlt nur die Spur"; `false` heisst
     * „der Leistungsstand haengt noch am Save".
     *
     * `false` auch dann, wenn der Server eine der Positionen als fehlend
     * gemeldet hat (`countersMissing`): ein Aufrufer, der „steht schon" meldet,
     * obwohl der Stand einer Position noch am Save haengt, schickt die
     * Buchhaltung zur falschen Korrektur.
     */
    countersWritten: boolean;
    /**
     * Positionen, zu denen der Server KEINEN Zaehler schreiben konnte, weil es
     * sie in `project_services` (noch) nicht gibt — Nr. 118 meldet sie als
     * `counters_missing` statt zu werfen (Review 23.09.2026, P2-2).
     *
     * Kein Fehler: es gibt Projekte, deren Kindtabellen unvollstaendig sind
     * (eine Position lebt nur im Blob). Der anschliessende
     * `saveProjectToCloud` legt die Zeile ueber `save_project_children_tx` an
     * und schreibt denselben Zaehler — wie auf jedem Weg ohne Nr. 118. Wuerfe
     * die RPC hier, verlore genau dieser Bestand JEDE Ruecknahme.
     *
     * Leer auf allen anderen Wegen (`rpc`, `legacy`, `noop`): dort schreibt der
     * Server ohnehin keine Zaehler.
     */
    countersMissing: string[];
}

/** Eine Protokollzeile, die stehen bleibt und nur noch `quantity` traegt. */
export interface ServiceLogShortening {
    id: string;
    /** Die NEUE Menge. Groesser 0 und kleiner als die bisherige. */
    quantity: number;
}

/** Ein Leistungsstand, den der Server mitschreiben soll. */
export interface ServiceLogCounterWrite {
    serviceId: string;
    quantityUsed: number;
}

/**
 * Was ueber die Entfernung hinaus in DERSELBEN Transaktion geschehen soll.
 *
 * Wird `opts` uebergeben — und sei es mit leerer id-Liste —, laeuft der Aufruf
 * ueber die sechsstellige Fassung aus Nr. 118. Ohne `opts` bleibt alles beim
 * Weg aus Nr. 66; das ist der Zustand der vier Rechnungspfade, die ihre Zaehler
 * weiter ueber den Blob-Save schreiben.
 */
export interface ServiceLogRemovalOptions {
    shortened?: ReadonlyArray<ServiceLogShortening>;
    counters?: ReadonlyArray<ServiceLogCounterWrite>;
}

/** Diff zweier Protokollstaende: was ist herausgefallen? */
export interface ServiceLogRemovalDiff {
    /** ids der Zeilen, die in `before` stehen und in `after` fehlen. */
    ids: string[];
    /**
     * Wie viele davon gar keine brauchbare id tragen.
     *
     * Ohne id ist eine Zeile weder fuer die RPC noch fuer das Journal
     * adressierbar. Sie faellt dann still ueber den Blob-Save weg (der Riegel
     * schuetzt ohnehin nur Zeilen MIT id) — aber niemand koennte sie
     * zurueckholen, deshalb wird die Zahl herausgegeben statt verschwiegen.
     */
    withoutId: number;
}

/** Leerraum weg, Leeres raus, Reihenfolge erhalten, keine Dubletten. */
export const normalizeEntryIds = (ids: ReadonlyArray<string | null | undefined>): string[] => {
    const out: string[] = [];
    for (const raw of ids ?? []) {
        const id = String(raw ?? '').trim();
        if (!id || out.includes(id)) continue;
        out.push(id);
    }
    return out;
};

/**
 * Welche Zeilen nimmt ein geplanter Stand aus dem Protokoll?
 *
 * Rein und ohne Supabase, damit die drei Pfade dieselbe Frage nicht dreimal
 * verschieden beantworten. Verglichen wird ueber die `id` und NICHT ueber den
 * Inhalt: ein Pfad, der einer Zeile nur die Buchungsfelder abnimmt
 * (`handleRestorePosition` bei LEGACY-AUTO-Eintraegen), aendert sie — er
 * entfernt sie nicht, und sie darf hier nicht auftauchen.
 */
export const collectRemovedEntryIds = (
    before: ReadonlyArray<ServiceTransaction> | null | undefined,
    after: ReadonlyArray<ServiceTransaction> | null | undefined,
): ServiceLogRemovalDiff => {
    const bleibt = new Set<string>();
    for (const t of after ?? []) {
        const id = String(t?.id ?? '').trim();
        if (id) bleibt.add(id);
    }

    const ids: string[] = [];
    let withoutId = 0;
    for (const t of before ?? []) {
        const id = String(t?.id ?? '').trim();
        if (!id) {
            // Zeile ohne id: sie kann in `after` gar nicht wiedererkannt
            // werden. Nur zaehlen, nie raten.
            withoutId += 1;
            continue;
        }
        if (!bleibt.has(id) && !ids.includes(id)) ids.push(id);
    }
    return { ids, withoutId };
};

/**
 * Der erwartete Reststand, nachdem der Server die ids entfernt hat.
 *
 * Genau das muss der anschliessende Save schreiben. Weicht der lokal
 * gerechnete Stand davon ab, schriebe der Save eine gerade entfernte Zeile
 * wieder hinein — der Riegel liesse das als Neuanlage durch, und die Buchung
 * stuende doppelt im Protokoll.
 */
export const serviceLogWithout = (
    log: ReadonlyArray<ServiceTransaction> | null | undefined,
    ids: ReadonlyArray<string | null | undefined>,
): ServiceTransaction[] => {
    const weg = new Set(normalizeEntryIds(ids));
    if (weg.size === 0) return [...(log ?? [])];
    return (log ?? []).filter((t) => {
        const id = String(t?.id ?? '').trim();
        return !id || !weg.has(id);
    });
};

/**
 * Fehlt die Funktion in der Datenbank? Wortgleich aus
 * `cloudService.isMissingRpc` — der Client wird VOR dem SQL ausgeliefert.
 */
const isMissingRpc = (error: any): boolean =>
    error?.code === 'PGRST202' || /could not find the function/i.test(error?.message || '');

/**
 * Hat Nr. 118 einen ueberholten Anker gemeldet?
 *
 * In erster Linie am SQLSTATE: `raise … using errcode = 'serialization_failure'`
 * kommt bei PostgREST als `code: '40001'` an. Der Text ist die zweite
 * Sicherung — ein Zwischenstueck, das den Code verschluckt, darf aus einem
 * Konflikt nicht „Speichern fehlgeschlagen" machen: dann wuesste der Anwender
 * nicht, dass Neuladen die Antwort ist.
 */
const isVersionConflict = (error: unknown): boolean => {
    const e = error as { code?: unknown; message?: unknown } | null;
    if (String(e?.code ?? '') === SERIALIZATION_FAILURE) return true;
    return /zwischenzeitlich gespeichert/i.test(String(e?.message ?? ''));
};

/**
 * Die Antwort der RPC lesen, ohne sich an einer Feldschreibweise aufzuhaengen.
 *
 * Vertrag (Nr. 66): `{ "removed": ["…"], "missing": ["…"] }`. Gelesen werden
 * daneben `removed_ids` / `not_found` / `missing_ids` und eine blanke Liste,
 * weil Client und SQL getrennt entstehen und ein Namensunterschied hier NICHT
 * dazu fuehren darf, dass eine gelungene Entfernung als Fehlschlag gilt.
 *
 * Sagt die Antwort gar nichts (null, leeres Objekt, unbekannte Form), gelten
 * alle angefragten ids als entfernt: die RPC hat OHNE Fehler geantwortet, und
 * das ist die Aussage, auf die es ankommt. Etwas anderes anzunehmen erfaende
 * einen Fehlschlag, den es nicht gab.
 */
export const parseRemovalPayload = (
    data: unknown,
    requestedIds: ReadonlyArray<string>,
): { removedIds: string[]; missingIds: string[] } => {
    const liste = (v: unknown): string[] | null =>
        Array.isArray(v) ? normalizeEntryIds(v as Array<string | null | undefined>) : null;

    if (Array.isArray(data)) {
        return { removedIds: liste(data) ?? [], missingIds: [] };
    }
    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        const removed = liste(obj.removed) ?? liste(obj.removed_ids) ?? liste(obj.entfernt);
        const missing =
            liste(obj.missing) ?? liste(obj.not_found) ?? liste(obj.missing_ids) ?? [];
        if (removed) return { removedIds: removed, missingIds: missing };
        if (missing.length) {
            const fehlt = new Set(missing);
            return {
                removedIds: requestedIds.filter((id) => !fehlt.has(id)),
                missingIds: missing,
            };
        }
    }
    return { removedIds: [...requestedIds], missingIds: [] };
};

/** Ein Ergebnis ohne Wirkung — die eine Form, damit kein Feld vergessen wird. */
const ohneWirkung = (mode: ServiceLogRemovalMode): ServiceLogRemovalOutcome => ({
    mode,
    removedIds: [],
    missingIds: [],
    shortenedIds: [],
    countersWritten: false,
    countersMissing: [],
});

/**
 * Die ids, die der Server GEKUERZT hat — falls er es sagt.
 *
 * `null` heisst „die Antwort nennt den Schluessel nicht"; der Aufrufer nimmt
 * dann seine eigene Liste. Dieselbe Haltung wie in `parseRemovalPayload`: die
 * RPC hat ohne Fehler geantwortet, und etwas anderes anzunehmen erfaende einen
 * Fehlschlag, den es nicht gab.
 */
export const shortenedFromRemovalPayload = (data: unknown): string[] | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const v = (data as Record<string, unknown>).shortened;
    return Array.isArray(v) ? normalizeEntryIds(v as Array<string | null | undefined>) : null;
};

/** Wie viele Zaehler der Server geschrieben hat — `null`, wenn er es nicht sagt. */
export const countersFromRemovalPayload = (data: unknown): number | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const v = (data as Record<string, unknown>).counters;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/**
 * Die Positionen, zu denen der Server KEINEN Zaehler schreiben konnte.
 *
 * Nennt die Antwort den Schluessel nicht (aeltere Fassung von Nr. 118, fremde
 * Form), gilt die leere Liste: nichts zu melden ist die Aussage, die eine
 * Antwort ohne Fehler hergibt. Die Gegenrichtung — „unbekannt heisst fehlend" —
 * machte aus jeder gelungenen Ruecknahme eine halbe.
 */
export const countersMissingFromRemovalPayload = (data: unknown): string[] => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
    const v = (data as Record<string, unknown>).counters_missing;
    return Array.isArray(v) ? normalizeEntryIds(v as Array<string | null | undefined>) : [];
};

/**
 * Die Kuerzungen pruefen, bevor irgendetwas geschrieben wird.
 *
 * Eine Menge, die keine Zahl ist, oder eine Kuerzung auf 0 waere ein
 * Programmierfehler des Aufrufers: die Datenbank weist beides ab, aber erst
 * NACH dem Netzzugriff und mit einer Meldung, die im Dialog nichts zu suchen
 * hat. Eine doppelte id ist ebenfalls ein Fehler und nicht eine Absicht — sie
 * liefe in der Datenbank auf zwei Kuerzungen derselben Zeile hinaus.
 */
const pruefeKuerzungen = (
    list: ReadonlyArray<ServiceLogShortening> | undefined,
): Array<{ id: string; quantity: number }> => {
    const out: Array<{ id: string; quantity: number }> = [];
    for (const raw of list ?? []) {
        const id = String(raw?.id ?? '').trim();
        if (!id) {
            throw new Error('Protokollzeile kürzen: ohne Kennung der Zeile nicht möglich.');
        }
        const quantity = Number(raw?.quantity);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            throw new Error(
                `Protokollzeile kürzen: für ${id} fehlt eine Restmenge größer 0. Eine Kürzung auf 0 ist eine Entfernung.`,
            );
        }
        if (out.some((e) => e.id === id)) {
            throw new Error(`Protokollzeile kürzen: die Kennung ${id} steht zweimal in der Liste.`);
        }
        out.push({ id, quantity });
    }
    return out;
};

/** Dieselbe Vorpruefung fuer die Zaehler. Negativ ist keine Menge. */
const pruefeZaehler = (
    list: ReadonlyArray<ServiceLogCounterWrite> | undefined,
): Array<{ service_id: string; quantity_used: number }> => {
    const out: Array<{ service_id: string; quantity_used: number }> = [];
    for (const raw of list ?? []) {
        const serviceId = String(raw?.serviceId ?? '').trim();
        if (!serviceId) {
            throw new Error('Leistungsstand schreiben: ohne Kennung der Position nicht möglich.');
        }
        const quantityUsed = Number(raw?.quantityUsed);
        if (!Number.isFinite(quantityUsed) || quantityUsed < 0) {
            throw new Error(
                `Leistungsstand schreiben: für die Position ${serviceId} fehlt eine Menge ab 0.`,
            );
        }
        if (out.some((e) => e.service_id === serviceId)) {
            throw new Error(
                `Leistungsstand schreiben: die Position ${serviceId} steht zweimal in der Liste.`,
            );
        }
        out.push({ service_id: serviceId, quantity_used: quantityUsed });
    }
    return out;
};

/**
 * Nimmt Protokollzeilen serverseitig aus `projects.data` — und mit `opts` in
 * DERSELBEN Transaktion auch die Kuerzung und die Leistungsstaende.
 *
 * MUSS vor dem Speichern des uebrigen Stands laufen — die Begruendung steht im
 * Kopf dieser Datei unter „DIE REIHENFOLGE".
 *
 * Wirft bei jedem echten Fehler. Der Aufrufer darf ihn nicht verschlucken: mit
 * ausgerollter Nr. 66 scheitert sonst gleich darauf der Save am Riegel, und
 * ein still weiterlaufender Vorgang behauptete eine Ruecknahme, die nicht
 * stattgefunden hat. Ein ueberholter Anker kommt als
 * `ProjectSaveConflictError` heraus (Nr. 118 meldet ihn mit SQLSTATE 40001,
 * der Weg aus Nr. 66 findet ihn in seiner Vorpruefung).
 */
export const removeServiceLogEntries = async (
    projectId: string,
    entryIds: ReadonlyArray<string | null | undefined>,
    reason: string,
    opts?: ServiceLogRemovalOptions,
): Promise<ServiceLogRemovalOutcome> => {
    const id = String(projectId ?? '').trim();
    if (!id) throw new Error('Protokollzeilen entfernen: ohne Projekt-Id nicht möglich.');

    // Vor dem ersten Netzzugriff: der Grund steht dauerhaft in der Datenbank
    // und ist spaeter die einzige Auskunft darueber, warum eine Buchung
    // verschwunden ist. Ein leerer Grund ist ein Programmierfehler.
    const grund = String(reason ?? '').trim();
    if (!grund) {
        throw new Error(
            'Protokollzeilen entfernen: Es fehlt eine Begründung. Sie wird dauerhaft protokolliert und muss den Vorgang benennen.',
        );
    }

    const ids = normalizeEntryIds(entryIds);

    if (opts) return await atomarEntfernen(id, ids, grund, opts);

    if (ids.length === 0) return ohneWirkung('noop');
    return await entfernenNachNr66(id, ids, grund);
};

/**
 * Der Weg aus Nr. 118: Versionspruefung, Entfernung, Kuerzung und Zaehler in
 * EINER Transaktion.
 *
 * Die Vorpruefung aus Nr. 66 (`assertProjectUnchanged`) entfaellt hier mit
 * Absicht: sie war der Ersatz fuer eine Versionsbedingung, die es serverseitig
 * nicht gab. Jetzt gibt es sie, und zwar unter Zeilensperre — eine zweite
 * Pruefung davor kostete einen Request und liesse genau das Fenster offen, das
 * sie schliessen soll (fremder Save zwischen Pruefung und RPC).
 */
const atomarEntfernen = async (
    id: string,
    ids: string[],
    grund: string,
    opts: ServiceLogRemovalOptions,
): Promise<ServiceLogRemovalOutcome> => {
    const kuerzungen = pruefeKuerzungen(opts.shortened);
    const zaehler = pruefeZaehler(opts.counters);

    // Nichts zu entfernen, nichts zu kuerzen, kein Zaehler: gar nicht rufen.
    // Der Server wuerde dasselbe antworten, aber ohne Request ist es billiger
    // und der Anker bleibt unangetastet.
    if (ids.length === 0 && kuerzungen.length === 0 && zaehler.length === 0) {
        return ohneWirkung('noop');
    }

    // `null` heisst „dieser Aufrufer kennt keinen Stand" — dann prueft die RPC
    // nicht, genauso opportunistisch wie `resolveSaveAnchor`.
    const erwartet = knownProjectVersion(id);

    const rueckfall = async (): Promise<ServiceLogRemovalOutcome> => {
        // Nr. 118 fehlt. Kuerzen und Zaehler uebernimmt wie bisher der Save,
        // die Entfernung geht ueber die dreistellige Fassung aus Nr. 66 (mit
        // deren Vorpruefung). Bewusst kein console.warn: das ist der geplante
        // Zustand zwischen Client-Deploy und SQL-Lauf.
        if (ids.length === 0) return ohneWirkung('legacy');
        const alt = await entfernenNachNr66(id, ids, grund);
        return { ...alt, shortenedIds: [], countersWritten: false, countersMissing: [] };
    };

    let data: unknown;
    try {
        const antwort = await supabase.rpc(SERVICE_LOG_REMOVAL_RPC, {
            p_project_id: id,
            p_entry_ids: ids,
            p_reason: grund,
            p_expected_updated_at: erwartet ?? null,
            p_shortened: kuerzungen,
            p_counters: zaehler,
        });
        if (antwort.error) {
            if (isMissingRpc(antwort.error)) return await rueckfall();
            if (isVersionConflict(antwort.error)) throw konflikt(id, erwartet);
            throw antwort.error;
        }
        data = antwort.data;
    } catch (e) {
        // Der eigene Wurf von oben geht unverändert durch.
        if (isProjectSaveConflict(e)) throw e;
        // Manche Clients werfen statt abzulehnen — dieselben Unterscheidungen
        // noch einmal, sonst schluege die fehlende Migration als echter Fehler
        // durch.
        if (isMissingRpc(e)) return await rueckfall();
        if (isVersionConflict(e)) throw konflikt(id, erwartet);
        throw e;
    }

    const { removedIds, missingIds } = parseRemovalPayload(data, ids);
    // Sagt die Antwort nichts ueber die Kuerzungen, gelten die angefragten als
    // gekuerzt — ausser denen, die der Server als nicht gefunden meldet.
    const shortenedIds = shortenedFromRemovalPayload(data)
        ?? kuerzungen.map((k) => k.id).filter((k) => !missingIds.includes(k));
    const geschrieben = countersFromRemovalPayload(data) ?? zaehler.length;
    const countersMissing = countersMissingFromRemovalPayload(data);

    if (missingIds.length > 0) {
        console.warn(
            `[serviceLogRemoval] ${missingIds.length} Protokollzeile(n) im Projekt ${id} nicht gefunden, nichts entfernt oder gekürzt (${grund}):`,
            missingIds,
        );
    }

    // Eine Position ohne relationale Zeile ist kein Fehler (der Save legt sie
    // an), aber sie darf nicht still bleiben: sie ist der Hinweis darauf, dass
    // die Kindtabellen dieses Projekts unvollstaendig sind.
    if (countersMissing.length > 0) {
        console.warn(
            `[serviceLogRemoval] Projekt ${id}: zu ${countersMissing.length} Position(en) gibt es keine Zeile in project_services — `
            + 'der Leistungsstand haengt dort weiter am Save:',
            countersMissing,
        );
    }

    // Der Anker kommt AUS DER ANTWORT: Nr. 118 gibt `updated_at` zurueck, und
    // damit ist das Fenster zwischen RPC und Nachlesen zu. Fehlt der Schluessel
    // (fremde Fassung, unbekannte Form), liest `refreshProjectAnchor` wie
    // bisher nach — lieber ein Request zu viel als ein ueberholter Anker.
    await refreshProjectAnchor(id, data);

    return {
        mode: 'atomic',
        removedIds,
        missingIds,
        shortenedIds,
        // Nur wenn ALLE Positionen geschrieben sind: eine fehlende haengt noch
        // am Save, und der Aufrufer meldet daran seinen Text.
        countersWritten: geschrieben > 0 && countersMissing.length === 0,
        countersMissing,
    };
};

/**
 * Der Konflikt, den Nr. 118 gemeldet hat.
 *
 * Der Anker wird NICHT vergessen, und das ist die tragende Entscheidung: ohne
 * Anker schriebe der naechste Aufruf `p_expected_updated_at = null`, die RPC
 * pruefte nichts mehr, und ein zweiter Klick ohne Neuladen entfernte Zeilen und
 * schriebe Zaehler, die auf dem ueberholten Stand gerechnet sind — genau der
 * Mechanismus von 26051025. `forgetProjectVersion` gehoert dem anderen Fall
 * („der Stand liess sich nicht lesen, also besser bedingungslos MIT Zaehlern
 * schreiben als still ohne"); hier ist der Stand bekannt und falsch.
 *
 * `actual` bleibt `null`: der Server nennt seinen Stand in der Meldung, aber
 * aus einem Fehlertext einen Zeitstempel zurueckzurechnen waere eine zweite,
 * unnoetige Vertragsflaeche. Der Aufrufer braucht ihn nicht — er meldet und
 * laesst neu laden.
 */
const konflikt = (id: string, erwartet: string | null): ProjectSaveConflictError =>
    new ProjectSaveConflictError(id, erwartet ?? 'unbekannt', null);

/**
 * Der Weg aus Nr. 66: Vorpruefung, Entfernung, Anker nachlesen. Unveraendert —
 * er bleibt der Rueckfall und der Weg der vier Rechnungspfade.
 */
const entfernenNachNr66 = async (
    id: string,
    ids: string[],
    grund: string,
): Promise<ServiceLogRemovalOutcome> => {
    // ── VOR der RPC: rechnet der Aufrufer noch auf dem Serverstand? ────────
    // Die RPC prueft keine Version. Hat seit dem Laden jemand gespeichert
    // (zweiter Tab, Warteschlange, Kollege), sind der geplante Reststand und
    // der Zaehler des Aufrufers auf einem ueberholten Stand gerechnet — und der
    // Anker, der unten nachgezogen wird, verdeckte genau diesen Konflikt: der
    // Save schriebe den alten Zaehler ueber den neuen (Codex-Review 22.09.,
    // P1). Deshalb hier abbrechen, BEVOR etwas entfernt ist; der Aufrufer
    // meldet, der Anwender laedt neu und wiederholt. Ohne bekannten Anker
    // (Aufrufer hat nicht ueber loadProjectById geladen) wie bisher.
    await assertProjectUnchanged(id);

    let data: unknown;
    try {
        const antwort = await supabase.rpc(SERVICE_LOG_REMOVAL_RPC, {
            p_project_id: id,
            p_entry_ids: ids,
            p_reason: grund,
        });
        if (antwort.error) {
            // Bewusst kein console.warn: das ist der geplante Zustand zwischen
            // Client-Deploy und SQL-Lauf und darf das Protokoll nicht fluten.
            if (isMissingRpc(antwort.error)) {
                return ohneWirkung('legacy');
            }
            throw antwort.error;
        }
        data = antwort.data;
    } catch (e) {
        // Manche Clients werfen statt abzulehnen — dieselbe Unterscheidung
        // noch einmal, sonst wuerde die fehlende Migration als echter Fehler
        // durchschlagen.
        if (isMissingRpc(e)) return ohneWirkung('legacy');
        throw e;
    }

    const { removedIds, missingIds } = parseRemovalPayload(data, ids);
    if (missingIds.length > 0) {
        console.warn(
            `[serviceLogRemoval] ${missingIds.length} Protokollzeile(n) im Projekt ${id} nicht gefunden, nichts entfernt (${grund}):`,
            missingIds,
        );
    }

    // ── NACH der RPC: den Sync-Anker nachziehen — auch wenn nichts entfernt wurde ──
    // Die RPC schreibt `projects.updated_at = now()` (service_log_lockdown.sql).
    // Der Aufrufer speichert gleich darauf den uebrigen Stand mit dem Anker
    // aus seinem Laden (projectVersionAnchor) — und der ist jetzt ueberholt.
    // Der Save liefe in die Bergung, und die Bergung laesst die Zaehler seit
    // dem 07.09.2026 beim Serverwert (`counters: 'keep-server'`): Zeile weg,
    // Zaehler bleibt. Projekt 26051025, 22.09.2026: dreimal „Buchung
    // zuruecknehmen", dreimal blieb erbracht = 4. Dieselbe Klasse wie Vorfall
    // 26073001 (Direkt-Update mit updated_at), nur auf dem Rueckweg. Trifft
    // jeden Aufrufer dieser Funktion: Ruecknahme, Storno der Abrechnung,
    // Rechnungs-Rueckschreibung, Alt-Rechnung — ueberall folgt ein Save, der
    // Zaehler senken will.
    //
    // Was bleibt, und zwar OHNE Bergung (Codex-Review 22.09., Runde 2): das
    // Fenster zwischen der RPC und diesem Lesen. Schreibt dort ein anderer
    // Client, uebernimmt das Lesen DESSEN Stand als Anker, und der folgende
    // Save des Aufrufers trifft ihn — mit einem Reststand, der auf dem Stand
    // VOR der Vorpruefung gerechnet ist. Der fremde Zaehler wuerde ueber-
    // schrieben. Das Fenster ist ein sequenzieller Request breit; ganz
    // schliessen kann es nur eine RPC, die ihren eigenen `updated_at`
    // zurueckgibt (dann liest `refreshProjectAnchor` gar nicht mehr) oder die
    // Entfernung und Zaehler mit Versionspruefung in EINER Transaktion
    // schreibt. Beides ist Nr. 118 — dieser Weg hier bleibt der RUECKFALL.
    // Das zweite Fenster, zwischen diesem Anker und dem Save des Aufrufers,
    // faengt die Bergung wie bisher (Konsolenmeldung).
    await refreshProjectAnchor(id, data);
    return {
        mode: 'rpc',
        removedIds,
        missingIds,
        shortenedIds: [],
        countersWritten: false,
        countersMissing: [],
    };
};

/**
 * Der Stand, den die RPC selbst meldet — falls sie es tut.
 *
 * Die dreistellige Fassung aus Nr. 66 gibt ihn nicht zurueck, die
 * sechsstellige aus Nr. 118 immer. Dort, wo er kommt, ist das Fenster zwischen
 * RPC und Nachlesen zu.
 */
export const updatedAtFromRemovalPayload = (data: unknown): string | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const v = (data as Record<string, unknown>).updated_at;
    return typeof v === 'string' && v.trim() ? v : null;
};

/** Wie oft der Stand nach der RPC gelesen wird, bevor der Anker aufgegeben wird. */
const ANKER_LESEVERSUCHE = 3;
/** Pause zwischen zwei Leseversuchen. */
const ANKER_LESEPAUSE_MS = 150;

/** `projects.updated_at` lesen; `null` = keine Zeile (RLS oder geloescht). Wirft bei Fehler. */
const readProjectVersion = async (projectId: string): Promise<string | null> => {
    const { data, error } = await supabase
        .from('projects')
        .select('updated_at')
        .eq('id', projectId)
        .maybeSingle();
    if (error) throw error;
    const updatedAt = (data as { updated_at?: unknown } | null)?.updated_at;
    return typeof updatedAt === 'string' && updatedAt.trim() ? updatedAt : null;
};

/**
 * Wirft `ProjectSaveConflictError`, wenn der Server einen anderen Stand fuehrt
 * als den, auf dem der Aufrufer rechnet. Ohne bekannten Anker keine Pruefung
 * (opportunistisch, wie resolveSaveAnchor). Ein Lesefehler wirft ebenfalls:
 * vor der RPC ist noch nichts geschehen, und die RPC scheiterte dann meist
 * am selben Netz.
 */
const assertProjectUnchanged = async (projectId: string): Promise<void> => {
    const erwartet = knownProjectVersion(projectId);
    if (!erwartet) return;
    const aktuell = await readProjectVersion(projectId);
    if (aktuell !== null && aktuell !== erwartet) {
        throw new ProjectSaveConflictError(projectId, erwartet, aktuell);
    }
};

/**
 * Den Anker auf den Stand setzen, den die RPC eben geschrieben hat.
 *
 * Laesst sich der Stand nicht lesen, wird der Anker VERGESSEN statt behalten:
 * ohne Anker schreibt der Save bedingungslos und MIT Zaehlern (der
 * „opportunistische" Weg in projectVersionAnchor). Ein behaltener, ueberholter
 * Anker liefe dagegen sicher in die Bergung — und die laesst genau die Zaehler
 * weg, die dieser Vorgang aendern will. Von beiden Fehlern ist der stille der
 * schlimmere; deshalb wird der Fall auch nicht still, sondern in der Konsole
 * gemeldet.
 */
const refreshProjectAnchor = async (projectId: string, payload: unknown): Promise<void> => {
    const ausAntwort = updatedAtFromRemovalPayload(payload);
    if (ausAntwort) {
        rememberProjectVersion(projectId, ausAntwort);
        return;
    }
    // Dreimal lesen, bevor der Anker aufgegeben wird (Codex-Review 22.09.,
    // Runde 3): ein einzelner Netzaussetzer soll nicht den Versionsschutz des
    // Folge-Saves kosten. Erst wenn auch der dritte Versuch nichts liefert,
    // gilt der Rueckfall.
    let grund = 'keine Projektzeile lesbar';
    for (let versuch = 1; versuch <= ANKER_LESEVERSUCHE; versuch++) {
        try {
            const aktuell = await readProjectVersion(projectId);
            if (aktuell) {
                rememberProjectVersion(projectId, aktuell);
                return;
            }
        } catch (e) {
            // Supabase wirft keine Error-Instanzen (schlichte { message, code, ... }
            // -Objekte) — describeError() holt die Meldung verlustfrei heraus statt
            // `[object Object]` zu riskieren (siehe errorMessage.ts).
            grund = describeError(e);
        }
        if (versuch < ANKER_LESEVERSUCHE) await new Promise((r) => setTimeout(r, ANKER_LESEPAUSE_MS));
    }
    console.warn(
        `[serviceLogRemoval] Projekt ${projectId}: Stand nach der Entfernung ${ANKER_LESEVERSUCHE}x nicht lesbar (${grund}) — `
        + 'Anker vergessen, der naechste Save schreibt ohne Versionsbedingung.',
    );
    forgetProjectVersion(projectId);
};
